import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Trophy, Lock, Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email.trim().toLowerCase(), password);
      if (user.role === "admin") {
        toast.success("Welcome back, Admin!");
        navigate("/admin123okay");
      } else {
        toast.error("Admin access required");
      }
    } catch {
      toast.error("Invalid credentials");
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Enter your email"); return; }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Reset link sent! Check your email.");
    } catch {
      toast.error("Failed to send reset link");
    } finally { setForgotLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center px-4" data-testid="login-page">
      <Card className="w-full max-w-md bg-white border-gray-200 shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Trophy className="h-8 w-8 text-green-600" />
            <span className="text-2xl font-bold logo-text">FIXEDRAWS</span>
          </div>
          <CardTitle className="text-xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Lock className="h-5 w-5" /> {forgotMode ? "Reset Password" : "Admin Login"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!forgotMode ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@fixedraws.com" className="h-11" data-testid="email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Password</Label>
                <div className="relative">
                  <Input value={password} onChange={e => setPassword(e.target.value)} type={showPassword ? "text" : "password"} placeholder="Enter password" className="h-11 pr-11" data-testid="password-input" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1} data-testid="toggle-password">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold" data-testid="login-submit-btn">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
              <button type="button" onClick={() => setForgotMode(true)} className="w-full text-sm text-green-600 hover:text-green-700 font-medium mt-2" data-testid="forgot-password-link">
                Forgot Password?
              </button>
            </form>
          ) : !forgotSent ? (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <p className="text-sm text-gray-500">Enter your admin email and we'll send you a password reset link.</p>
              <div className="space-y-2">
                <Label className="text-gray-700">Email</Label>
                <Input value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} type="email" placeholder="Enter your admin email" className="h-11" data-testid="forgot-email-input" />
              </div>
              <Button type="submit" disabled={forgotLoading} className="w-full h-11 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold" data-testid="send-reset-btn">
                {forgotLoading ? "Sending..." : "Send Reset Link"}
              </Button>
              <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }} className="w-full text-sm text-gray-500 hover:text-gray-700 mt-2" data-testid="back-to-login">
                Back to Login
              </button>
            </form>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Lock className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Check Your Email</h3>
              <p className="text-sm text-gray-500 mb-6">A password reset link has been sent to <strong>{forgotEmail}</strong></p>
              <button type="button" onClick={() => { setForgotMode(false); setForgotSent(false); }} className="text-sm text-green-600 hover:text-green-700 font-medium" data-testid="back-to-login-2">
                Back to Login
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
